import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import { hospitalService } from '@/services/hospitalService';
import { clinicService } from '@/services/clinicService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, ShieldCheck } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const ClinicSelectionPage = () => {
  const navigate = useNavigate();
  const { hospitalId: routeHospitalId } = useParams();
  const { user, logout } = useAuth();
  const {
    hospitalId: contextHospitalId,
    setActiveClinicId,
    setAvailableClinics,
  } = useClinic();

  const currentHospitalId = routeHospitalId || contextHospitalId || (typeof window !== 'undefined' ? sessionStorage.getItem('pulseflow_hospital_id') : '');
  const [clinics, setClinics] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentHospitalId) {
      toast.error('Hospital context missing. Please login again.');
      navigate('/login', { replace: true });
      return;
    }

    const loadClinics = async () => {
      setLoading(true);
      try {
        let list = [];
        if (user?.assignedClinics && Array.isArray(user.assignedClinics) && user.assignedClinics.length > 0) {
          list = user.assignedClinics;
        } else {
          list = await clinicService.getHospitalClinics(currentHospitalId);
        }

        // Normalize clinic objects
        const normalized = list.map((c) => {
          if (typeof c === 'string') {
            return { id: c, name: `Clinic #${c}` };
          }
          return { id: c.id || c.clinicId, name: c.name || `Clinic #${c.id || c.clinicId}` };
        });

        setClinics(normalized);
        setAvailableClinics(normalized);

        // Auto-redirect and sync if only 1 clinic is assigned
        if (normalized.length === 1) {
          const single = normalized[0];
          setActiveClinicId(single.id);
          clinicService.syncClinic(currentHospitalId, single).catch(() => {});
          navigate(`/hospital/${currentHospitalId}/clinic/${single.id}/dashboard`, { replace: true });
          return;
        }

        if (normalized.length > 0) {
          setSelectedClinicId(normalized[0].id);
        }
      } catch (err) {
        console.error('Failed to load clinics:', err);
        toast.error('Failed to load assigned clinics');
      } finally {
        setLoading(false);
      }
    };

    loadClinics();
  }, [currentHospitalId, user, navigate, setActiveClinicId, setAvailableClinics]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedClinicId) {
      toast.error('Please select a clinic');
      return;
    }
    const selected = clinics.find((c) => c.id === selectedClinicId) || { id: selectedClinicId };
    setActiveClinicId(selectedClinicId);
    clinicService.syncClinic(currentHospitalId, selected).catch(() => {});
    navigate(`/hospital/${currentHospitalId}/clinic/${selectedClinicId}/dashboard`);
  };

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex bg-slate-950">
      {/* Left Column - Visual Hero Section */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-slate-950 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center scale-105 opacity-35"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1200&q=80)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950 via-slate-950/85 to-teal-950/70" />

        <div className="relative z-10 p-12 flex flex-col justify-between h-full text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-teal-500 flex items-center justify-center shadow-lg shadow-teal-500/30 text-slate-950 font-bold">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <span className="font-heading font-bold text-2xl text-white tracking-tight">PulseFlow</span>
              <p className="text-xs text-teal-400 font-semibold uppercase tracking-wider">
                Outpatient Clinical Network
              </p>
            </div>
          </div>

          <div className="max-w-md space-y-4">
            <Badge className="bg-teal-500/20 text-teal-300 border-teal-500/30 text-xs px-3 py-1">
              Hospital Entity #{currentHospitalId}
            </Badge>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white leading-snug">
              Select Your Clinical Station
            </h1>
            <p className="text-sm text-slate-300 leading-relaxed">
              Outpatient appointments, live triage queues, and electronic health records will be dynamically scoped to your selected clinic.
            </p>
          </div>

          <div className="flex items-center justify-between text-xs text-slate-400 border-t border-slate-800/80 pt-4">
            <span>© 2026 PulseFlow Healthcare Solutions</span>
            <span className="flex items-center gap-1.5 text-teal-400">
              <ShieldCheck className="h-4 w-4" /> ABDM & HIPAA Aligned
            </span>
          </div>
        </div>
      </div>

      {/* Right Column - Clinic Dropdown Card */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12 bg-slate-50">
        <div className="w-full max-w-md space-y-6">
          <Card className="border border-slate-200/80 bg-white text-slate-900 rounded-2xl shadow-xl">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-xl font-heading font-bold text-slate-900 text-center">
                Select Clinic
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="clinic" className="text-xs font-semibold text-slate-700">
                    Clinic
                  </Label>
                  <Select
                    value={selectedClinicId}
                    onValueChange={setSelectedClinicId}
                    disabled={loading || clinics.length === 0}
                  >
                    <SelectTrigger className="w-full h-10 border-slate-200 bg-white text-slate-900 focus:ring-teal-500 text-xs">
                      <SelectValue placeholder={loading ? 'Loading clinics...' : 'Select a clinic'} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 text-slate-900 shadow-lg">
                      {clinics.map((c) => (
                        <SelectItem
                          key={c.id}
                          value={c.id}
                          className="text-xs focus:bg-teal-50 focus:text-teal-900"
                        >
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !selectedClinicId}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold h-10 text-xs shadow-md"
                >
                  Continue
                </Button>
              </form>

              <div className="pt-2 text-center">
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="text-xs text-slate-500 hover:text-red-600 transition-colors"
                >
                  Sign Out
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClinicSelectionPage;
